import React, { useState, useRef } from 'react';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import './BulkAddDevicesModal.css';

const BulkAddDevicesModal = ({ onClose, onBulkAdd }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmIsError, setConfirmIsError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [processingResults, setProcessingResults] = useState({ success: 0, failed: 0, errors: [] });
  const fileInputRef = useRef(null);

  // Expected CSV columns based on AddDeviceModal fields
  const expectedColumns = [
    'SerialNumber',
    'DeviceName', 
    'ParentSerialNumber'
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      parseCSV(file);
    } else {
      setConfirmMessage('Please select a valid CSV file.');
      setConfirmIsError(true);
      setShowConfirmModal(true);
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setConfirmMessage('CSV file must have at least a header row and one data row.');
        setConfirmIsError(true);
        setShowConfirmModal(true);
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const dataRows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      // Validate headers
      const missingHeaders = expectedColumns.filter(col => !headers.includes(col));
      if (missingHeaders.length > 0) {
        setConfirmMessage(`Missing required columns: ${missingHeaders.join(', ')}`);
        setConfirmIsError(true);
        setShowConfirmModal(true);
        return;
      }

      // Validate data
      const errors = validateCSVData(dataRows);
      setValidationErrors(errors);
      setCsvData(dataRows);
    };
    reader.readAsText(file);
  };

  const validateCSVData = (data) => {
    const errors = [];
    
    data.forEach((row, index) => {
      const rowNum = index + 2; // +2 because we start from row 2 (after header)
      
      // Required field validation
      if (!row.SerialNumber || !row.SerialNumber.trim()) {
        errors.push(`Row ${rowNum}: Serial number is required`);
      }
      
      if (!row.DeviceName || !row.DeviceName.trim()) {
        errors.push(`Row ${rowNum}: Device name is required`);
      }

      // Serial number prefix validation and device kind derivation
      if (row.SerialNumber && row.SerialNumber.trim()) {
        const prefix = row.SerialNumber.slice(0, 5).toUpperCase();
        const expectedKind = getExpectedKindFromPrefix(prefix);
        if (!expectedKind) {
          errors.push(`Row ${rowNum}: Invalid serial number prefix '${prefix}'. Must start with IL_IB, IL_BX, or IL_LA`);
        }
      }

      // Parent serial number validation
      if (row.ParentSerialNumber && row.ParentSerialNumber.trim()) {
        const parentPrefix = row.ParentSerialNumber.slice(0, 5).toUpperCase();
        if (parentPrefix !== 'IL_IB') {
          errors.push(`Row ${rowNum}: Only iBeam devices (prefix IL_IB) can be parent devices`);
        }
        
        // Check if the current device can be a sub-device based on its serial number prefix
        if (row.SerialNumber && row.SerialNumber.trim()) {
          const currentPrefix = row.SerialNumber.slice(0, 5).toUpperCase();
          const currentKind = getExpectedKindFromPrefix(currentPrefix);
          if (currentKind && !['latch', 'lock'].includes(currentKind)) {
            errors.push(`Row ${rowNum}: Only latch and lock devices can be sub-devices`);
          }
        }
      }

    });

    return errors;
  };

  const getExpectedKindFromPrefix = (prefix) => {
    switch (prefix) {
      case 'IL_IB': return 'beam';
      case 'IL_BX': return 'lock';
      case 'IL_LA': return 'latch';
      default: return null;
    }
  };

  const getDeviceKindLabel = (prefix) => {
    switch (prefix) {
      case 'IL_IB': return 'iBeam';
      case 'IL_BX': return 'iLock Blue';
      case 'IL_LA': return 'Latch';
      default: return 'Unknown';
    }
  };

  const handleBulkAdd = async () => {
    if (validationErrors.length > 0) {
      setConfirmMessage(`Please fix ${validationErrors.length} validation errors before proceeding.`);
      setConfirmIsError(true);
      setShowConfirmModal(true);
      return;
    }

    setIsProcessing(true);
    const results = { success: 0, failed: 0, errors: [] };

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      try {
        // Derive device kind from serial number prefix
        const prefix = row.SerialNumber.slice(0, 5).toUpperCase();
        const deviceKind = getExpectedKindFromPrefix(prefix);
        
        const deviceData = {
          SerialNumber: row.SerialNumber.trim(),
          DeviceName: row.DeviceName.trim(),
          Kind: deviceKind,
          ParentSerialNumber: row.ParentSerialNumber ? row.ParentSerialNumber.trim() : ''
        };

        await apiClient.post('/devices', deviceData);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`Row ${i + 2}: ${error.response?.data || error.message}`);
      }
    }

    setProcessingResults(results);
    setIsProcessing(false);
    
    if (results.failed === 0) {
      setConfirmMessage(`Successfully added ${results.success} devices!`);
      setConfirmIsError(false);
    } else {
      setConfirmMessage(`Added ${results.success} devices successfully. ${results.failed} failed.`);
      setConfirmIsError(false);
    }
    setShowConfirmModal(true);
  };

  const downloadTemplate = () => {
    const headers = expectedColumns.join(',');
    const sampleData = [
      'IL_LA123456789,Latch Device 1,',
      'IL_BX987654321,iLock Blue Device 1,',
      'IL_IB555666777,iBeam Parent,',
      'IL_LA111222333,Sub Latch Device,IL_IB555666777'
    ].join('\n');
    
    const csvContent = `${headers}\n${sampleData}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'device_bulk_add_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setCsvFile(null);
    setCsvData([]);
    setValidationErrors([]);
    setProcessingResults({ success: 0, failed: 0, errors: [] });
    onClose();
  };

  return (
    <>
      <div className="bulk-add-backdrop">
        <div className="bulk-add-modal">
          <h2>Bulk Add Devices</h2>
          
          <div className="bulk-add-section">
            <h3>CSV Template</h3>
            <p>Download the CSV template to see the required format. Device kind is automatically derived from the serial number prefix:</p>
            <ul style={{ margin: '8px 0', paddingLeft: '20px', color: '#666' }}>
              <li><strong>IL_IB</strong> → iBeam (beam)</li>
              <li><strong>IL_BX</strong> → iLock Blue (lock)</li>
              <li><strong>IL_LA</strong> → Latch (latch)</li>
            </ul>
            <button className="template-download-button" onClick={downloadTemplate}>
              Download Template
            </button>
          </div>

          <div className="bulk-add-section">
            <h3>Upload CSV File</h3>
            <div className="file-upload-area">
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button 
                className="file-select-button"
                onClick={() => fileInputRef.current?.click()}
              >
                {csvFile ? csvFile.name : 'Select CSV File'}
              </button>
            </div>
          </div>

          {csvData.length > 0 && (
            <div className="bulk-add-section">
              <h3>Data Preview ({csvData.length} devices)</h3>
              <div className="data-preview">
                <table>
                  <thead>
                    <tr>
                      {expectedColumns.map(col => (
                        <th key={col}>{col}</th>
                      ))}
                      <th>Derived Kind</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 5).map((row, index) => {
                      const prefix = row.SerialNumber ? row.SerialNumber.slice(0, 5).toUpperCase() : '';
                      const derivedKind = getDeviceKindLabel(prefix);
                      return (
                        <tr key={index}>
                          {expectedColumns.map(col => (
                            <td key={col}>{row[col] || '—'}</td>
                          ))}
                          <td>{derivedKind}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {csvData.length > 5 && (
                  <p className="preview-note">... and {csvData.length - 5} more rows</p>
                )}
              </div>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="bulk-add-section">
              <h3>Validation Errors ({validationErrors.length})</h3>
              <div className="validation-errors">
                {validationErrors.map((error, index) => (
                  <div key={index} className="error-item">{error}</div>
                ))}
              </div>
            </div>
          )}

          {processingResults.errors.length > 0 && (
            <div className="bulk-add-section">
              <h3>Processing Errors</h3>
              <div className="processing-errors">
                {processingResults.errors.map((error, index) => (
                  <div key={index} className="error-item">{error}</div>
                ))}
              </div>
            </div>
          )}

          <div className="bulk-add-actions">
            <button onClick={handleClose} className="bulk-add-button">
              Cancel
            </button>
            <button 
              onClick={handleBulkAdd} 
              className="bulk-add-button primary"
              disabled={csvData.length === 0 || validationErrors.length > 0 || isProcessing}
            >
              {isProcessing ? 'Processing...' : `Add ${csvData.length} Devices`}
            </button>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={confirmMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={confirmIsError}
          onConfirm={() => {
            setShowConfirmModal(false);
            if (!confirmIsError && processingResults.failed === 0) {
              handleClose();
            }
          }}
        />
      )}
    </>
  );
};

export default BulkAddDevicesModal;
